import { cn } from 'cn';
import { type PropsWithChildren, type ReactNode, useCallback } from 'react';

import { Tooltip } from '../Tooltip';

type P = PropsWithChildren<{
  readonly icon?: ReactNode;
  readonly onError?: (error: unknown) => void;
  readonly onSuccess?: () => void;
  readonly tooltipText?: string;
  readonly value: string;
}> &
  ComponentStyling;

/**
 * A component that allows the user to copy a value to the clipboard.
 *
 * @param children - The content to display inside the component.
 * @param className - The class name to apply to the component.
 * @param icon - Optional icon to display.
 * @param onSuccess - Optional function to call when the value is copied to the clipboard.
 * @param onError - Optional function to call when the value fails to be copied to the clipboard.
 * @param style - Optional style to apply to the component.
 * @param tooltipText - Optional text to display in the tooltip.
 * @param value - The value to copy to the clipboard.
 * @returns A component that allows the user to copy a value to the clipboard.
 */
export const CopyToClipboard = ({
  children,
  className,
  icon,
  onSuccess,
  onError,
  style = {},
  tooltipText,
  value,
}: P) => {
  const onCopyClick = useCallback(async () => {
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API not available');
      }

      await navigator.clipboard.writeText(String(value ?? ''));
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  }, [value, onSuccess, onError]);

  return (
    <Tooltip content={tooltipText}>
      <span
        className={cn(
          'flex cursor-copy items-center gap-1 focus:outline-none',
          className,
        )}
        role="button"
        style={style}
        tabIndex={0}
        onClick={onCopyClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onCopyClick();
          }
        }}
      >
        {children}
        {icon}
      </span>
    </Tooltip>
  );
};
